<template>
    <div>
        <div class="editor" ref="editor" :style="styles"></div>
        <el-dialog
            title="调整图片尺寸"
            :visible.sync="dialogVisible"
            width="30%"
            append-to-body
            :before-close="handleClose"
        >
            <div class="image-resize-dialog">
                <el-form label-width="80px">
                    <el-form-item label="宽度">
                        <el-input-number
                            v-model="imageWidth"
                            :min="50"
                            :max="1000"
                        ></el-input-number>
                    </el-form-item>
                    <el-form-item label="高度">
                        <el-input-number
                            v-model="imageHeight"
                            :min="50"
                            :max="1000"
                        ></el-input-number>
                    </el-form-item>
                </el-form>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" @click="confirmResize"
                    >确定</el-button
                >
            </span>
        </el-dialog>
    </div>
</template>

<script>
import Quill from "quill";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";

// 引入中文语言包
const zhCN = {
    placeholder: "请输入内容...",
    toolbar: {
        bold: "粗体",
        italic: "斜体",
        underline: "下划线",
        strike: "删除线",
        blockquote: "引用",
        "code-block": "代码块",
        header: "标题",
        list: {
            ordered: "有序列表",
            bullet: "无序列表",
        },
        script: {
            sub: "下标",
            super: "上标",
        },
        indent: {
            "+1": "增加缩进",
            "-1": "减少缩进",
        },
        direction: "文本方向",
        size: {
            small: "小号",
            large: "大号",
            huge: "超大",
        },
        header: {
            1: "标题1",
            2: "标题2",
            3: "标题3",
            4: "标题4",
            5: "标题5",
            6: "标题6",
        },
        color: "文字颜色",
        background: "背景颜色",
        font: {
            serif: "衬线字体",
            monospace: "等宽字体",
        },
        align: {
            center: "居中对齐",
            right: "右对齐",
            justify: "两端对齐",
        },
        clean: "清除格式",
        image: "图片",
    },
};

export default {
    props: {
        value: {
            type: String,
            default: "",
        },
        /* 高度 */
        height: {
            type: Number,
            default: null,
        },
        /* 最小高度 */
        minHeight: {
            type: Number,
            default: 200,
        },
    },
    data() {
        return {
            quill: null,
            dialogVisible: false,
            imageWidth: 200,
            imageHeight: 200,
            currentImage: null,
            content: this.value,
        };
    },
    computed: {
        styles() {
            let style = {};
            if (this.minHeight) {
                style.minHeight = `${this.minHeight}px`;
            }
            if (this.height) {
                style.height = `${this.height}px`;
            }
            return style;
        },
    },
    watch: {
        value: {
            handler(newVal) {
                if (this.quill && newVal !== this.content) {
                    this.content = newVal;
                    this.quill.root.innerHTML = this.content;
                }
            },
            immediate: true,
        },
    },
    mounted() {
        // 注册中文语言包
        Quill.register("translation", zhCN);

        this.quill = new Quill(this.$refs.editor, {
            theme: "snow",
            placeholder: "请输入内容...",
            modules: {
                toolbar: {
                    container: [
                        ["bold", "italic", "underline", "strike"], // 加粗 斜体 下划线 删除线
                        ["blockquote", "code-block"], // 引用  代码块
                        [{ list: "ordered" }, { list: "bullet" }], // 有序、无序列表
                        [{ indent: "-1" }, { indent: "+1" }], // 缩进
                        [{ size: ["small", false, "large", "huge"] }], // 字体大小
                        [{ header: [1, 2, 3, 4, 5, 6, false] }], // 标题
                        [{ color: [] }, { background: [] }], // 字体颜色、字体背景颜色
                        [{ align: [] }], // 对齐方式
                        ["clean"], // 清除文本格式
                        ["image"], // 图片
                    ],
                    handlers: {
                        image: this.imageHandler,
                    },
                },
            },
        });

        // 初始化内容
        if (this.content) {
            this.quill.root.innerHTML = this.content;
        }

        // 监听内容变化
        this.quill.on("text-change", () => {
            const html = this.quill.root.innerHTML;
            this.content = html;
            this.$emit("input", html);
            this.$emit("update:value", html);
        });
    },
    methods: {
        imageHandler() {
            const input = document.createElement("input");
            input.setAttribute("type", "file");
            input.setAttribute("accept", "image/*");
            input.click();

            input.onchange = async () => {
                const file = input.files[0];
                if (!file) return;

                const imageUrl = await this.uploadImage(file);
                const range = this.quill.getSelection();
                this.quill.insertEmbed(
                    range.index,
                    "image",
                    imageUrl,
                    Quill.sources.USER
                );

                // 延迟选择图片以获取图片节点
                setTimeout(() => {
                    const imgNode = this.$refs.editor.querySelector(
                        'img[src="' + imageUrl + '"]'
                    );
                    if (imgNode) {
                        imgNode.classList.add("custom-image");
                        this.currentImage = imgNode;
                        // 获取图片当前尺寸
                        this.imageWidth = imgNode.width || 200;
                        this.imageHeight = imgNode.height || 200;
                        this.dialogVisible = true;
                    }
                }, 0);
            };
        },
        async uploadImage(file) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    resolve(e.target.result);
                };
                reader.readAsDataURL(file);
            });
        },
        handleClose(done) {
            this.dialogVisible = false;
            done();
        },
        confirmResize() {
            if (this.currentImage) {
                this.currentImage.style.width = `${this.imageWidth}px`;
                this.currentImage.style.height = `${this.imageHeight}px`;
            }
            this.dialogVisible = false;
        },
    },
};
</script>

<style>
.custom-image {
    display: inline-block;
    max-width: 100%;
}

.image-resize-dialog {
    padding: 20px;
}

/* 工具栏样式优化 */
.ql-toolbar.ql-snow {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 8px;
    gap: 5px;
}

.ql-toolbar.ql-snow .ql-formats {
    display: flex;
    align-items: center;
    margin-right: 10px;
}

/* 下拉菜单样式修复 */
.ql-snow .ql-picker {
    position: relative;
}

.ql-snow .ql-picker-options {
    min-width: 100%;
    padding: 4px 8px;
    background-color: #fff;
    border: 1px solid #ccc;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.ql-snow .ql-picker-label {
    padding: 2px 4px;
}

.ql-snow .ql-picker-options .ql-picker-item {
    padding: 4px 8px;
    white-space: nowrap;
}

.editor,
.ql-toolbar {
    white-space: pre-wrap !important;
    line-height: normal !important;
}
.quill-img {
    display: none;
}
.ql-container {
    font-size: 14px;
}
.ql-snow .ql-tooltip[data-mode="link"]::before {
    content: "请输入链接地址:";
}
.ql-snow .ql-tooltip.ql-editing a.ql-action::after {
    border-right: 0px;
    content: "保存";
    padding-right: 0px;
}
.ql-snow .ql-tooltip[data-mode="video"]::before {
    content: "请输入视频地址:";
}
.ql-snow .ql-picker.ql-size .ql-picker-label::before,
.ql-snow .ql-picker.ql-size .ql-picker-item::before {
    content: "14px";
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="small"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="small"]::before {
    content: "10px";
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="large"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="large"]::before {
    content: "18px";
}
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="huge"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="huge"]::before {
    content: "32px";
}
.ql-snow .ql-picker.ql-header .ql-picker-label::before,
.ql-snow .ql-picker.ql-header .ql-picker-item::before {
    content: "文本";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before {
    content: "标题1";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before {
    content: "标题2";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before {
    content: "标题3";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="4"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="4"]::before {
    content: "标题4";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="5"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="5"]::before {
    content: "标题5";
}
.ql-snow .ql-picker.ql-header .ql-picker-label[data-value="6"]::before,
.ql-snow .ql-picker.ql-header .ql-picker-item[data-value="6"]::before {
    content: "标题6";
}
.ql-snow .ql-picker.ql-font .ql-picker-label::before,
.ql-snow .ql-picker.ql-font .ql-picker-item::before {
    content: "标准字体";
}
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="serif"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="serif"]::before {
    content: "衬线字体";
}
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="monospace"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="monospace"]::before {
    content: "等宽字体";
}
.ql-container.ql-snow {
    /* border: none; */
    overflow-y: auto;
}
.ql-toolbar.ql-snow {
    /* border: none; */
}
</style>
